import { fromEvent, of, BehaviorSubject } from 'rxjs';
import type { Observable } from 'rxjs';
import { switchMap, distinctUntilChanged, filter, map, take, withLatestFrom, tap } from 'rxjs/operators';
import { SPREADSHEET_API_URL } from 'settings';
import type { IHint, IHintConfig, EHintPath } from 'v2/interfaces/hint';
import apiService from 'v2/services/api';

/**
 * Service responsible for controlling `Hint` component.
 *
 * @class HintService
 */
class HintService {
  private scroll$ = new BehaviorSubject<number>(0);
  private focus$ = new BehaviorSubject<string>(null);
  private hints$ = new BehaviorSubject<IHint[]>([]);
  private hintsConfig$ = new BehaviorSubject<IHintConfig[]>([]);

  /**
   * @constructor Observe the events `onFocus` and `onScroll` on DOM
   */
  constructor() {
    this.onScrollY().subscribe(scroll => {
      this.scroll$.next(scroll);
    });

    this.setVisibleHint().subscribe(hints => {
      this.hints$.next(hints);
    });

    this.onFocus().subscribe(element => {
      this.focus$.next(element.name || null);
    });

    this.setFocusedHint().subscribe(hints => {
      this.hints$.next(hints);
    });
  }
  /**
   * @method loadHint
   * @return {Observable} an array of `IHintConfig` with the enabled configs on spreadsheet
   * returned by the API
   */
  public loadHint = (path: EHintPath): void => {
    apiService
      .get<IHintConfig[]>(`${SPREADSHEET_API_URL}/hints/${path}`)
      .pipe(map(hintsConfig => hintsConfig.filter(config => config.enabled === true)))
      .subscribe(hintsConfig => {
        this.hintsConfig$.next(hintsConfig);
      });
  };

  /**
   * @method getHint
   * @param {string} name which represents the `input=['name']` that is passed to the hint component
   * @return {Observable} a `IHintConfig` with the config corresponding to the name
   */
  public getHint = (name: string): Observable<IHintConfig> =>
    this.hintsConfig$.pipe(
      map(hintsConfig => hintsConfig.find(hintConfig => hintConfig.name === name)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );

  /**
   * @method hasHint
   * @param {string} name which represents the `input=['name']` that is passed to the hint component
   * @return {Observable} a boolean that checks if the component should be displayed or not
   * according to the given name
   */
  public hasHint = (name: string): Observable<boolean> => {
    return this.hints$.asObservable().pipe(
      map(hints => {
        const hintFocused = hints.find(hint => hint.isFocused === true);
        const hintHovered = hints.find(hint => hint.isHovered === true);

        if (hintFocused && hintFocused.name === name && hintFocused.isVisible) {
          return true;
        }

        if (hintFocused && hintFocused.isVisible) {
          return false;
        }

        if (hintHovered && hintHovered.name === name && hintHovered.isVisible) {
          return true;
        }

        return false;
      })
    );
  };

  /**
   * @method setHint responsible for adding a new hint when the component is loaded
   * @internal do not use outside of `Hint` component
   * @param {string} name which represents the `input=['name']` that is passed to the hint component
   */
  public setHint = (name: string): void => {
    this.hints$
      .asObservable()
      .pipe(
        take(1),
        filter(hints => !hints.find(hint => hint.name === name)),
        map(hints => {
          return [...hints, { name, isFocused: false, isHovered: false, isVisible: false }];
        })
      )
      .subscribe(hints => {
        this.hints$.next(hints);
        this.scroll$.next(1);
      });
  };

  /**
   * @method removeHint responsible for remove a hint
   * @internal do not use outside of `Hint` component
   * @param {string} name which represents the `input=['name']` that is passed to the hint component
   */
  public removeHint = (name: string): void => {
    this.hints$
      .asObservable()
      .pipe(
        take(1),
        map(hints => hints.filter(hint => hint.name !== name))
      )
      .subscribe(hints => this.hints$.next(hints));
  };

  /**
   * @method onMouseEnter update hovered hint when component detects the event
   * @internal do not use outside of `Hint` component
   * @param {string} name which represents the `input=['name']` that is passed to the hint component
   */
  public onMouseEnter = (name: string) => {
    return this.hints$.asObservable().pipe(
      take(1),
      map(hints => hints.map(hint => ({ ...hint, isHovered: hint.name === name ? true : false }))),
      tap(hints => this.hints$.next(hints))
    );
  };

  /**
   * @method onMouseLeave remove all hovered hints when component detects the event
   * @internal do not use outside of `Hint` component
   */
  public onMouseLeave = () => {
    return this.hints$.asObservable().pipe(
      take(1),
      map(hints => hints.map(hint => ({ ...hint, isHovered: false }))),
      tap(hints => this.hints$.next(hints))
    );
  };

  public getHints = (path: EHintPath) => {
    return apiService
      .get<IHintConfig[]>(`${SPREADSHEET_API_URL}/hints/${path}`)
      .pipe(map(hintsConfig => hintsConfig.filter(config => config.enabled)));
  };

  /**
   * @method onScrollY
   * @return {Observable} a number representing the vertical positioning of the scroll
   */
  private onScrollY = (): Observable<number> => {
    return fromEvent(document, 'scroll').pipe(
      map(() => window.pageYOffset),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  };

  /**
   * @method setVisibleHint observe the `onScroll` event to update visible hints
   * @return {Observable} an array of `IHint` enabling all visible hints
   * on the page
   */
  private setVisibleHint = (): Observable<IHint[]> => {
    return this.scroll$.asObservable().pipe(
      switchMap(() => this.hints$.asObservable().pipe(take(1))),
      map(hints => {
        const newHints = hints.map(hint => {
          let isVisible = false;
          const el = document.getElementsByName(hint.name)[0];

          if (el) {
            const rect = el.getBoundingClientRect();

            const elemTop = rect.top;
            const elemBottom = rect.bottom;

            isVisible = elemTop >= 100 && elemBottom <= window.innerHeight - 100;
          }

          return { ...hint, isVisible };
        });
        return newHints;
      })
    );
  };

  /**
   * @method onFocus
   * @return {Observable} with the last element focused on the page
   */
  private onFocus = (): Observable<HTMLInputElement> => {
    return fromEvent(document, 'click').pipe(switchMap(() => of(document.activeElement as HTMLInputElement)));
  };

  /**
   * @method setFocusedHint observe the onFocus event to set a focused hint
   * @return {Observable} an array of `IHint` enabling the current focused hint
   * on the page
   */
  private setFocusedHint = (): Observable<IHint[]> => {
    return this.focus$.asObservable().pipe(
      distinctUntilChanged(),
      withLatestFrom(this.hints$),
      map(([name, hints]) => {
        return hints.map(hint => {
          if (hint.name === name) {
            return { ...hint, isFocused: true };
          }

          return { ...hint, isFocused: false };
        });
      })
    );
  };
}

const hintServiceFactory = new HintService();
export const hintFactory = hintServiceFactory;
